import { parseJson } from 'legacy/lut/classes/helpers/JsonHelper'
import { EltType } from 'legacy/lut/classes/models/partner360/ELT/EltType'
import { FORM_CODE, FORM_ID, IELTData } from 'legacy/lut/classes/models/partner360/ELT/IELTData'
import {
  ELT_FORM_SECTION,
  IEltDocFormData,
  IELTFormData,
  TComplexSectionData,
  TEltFormData,
} from 'legacy/lut/classes/models/partner360/ELT/IELTFormData'
import { IPartnerGroupInfo } from 'legacy/lut/classes/models/partner360/IPartnerGroup'
import { IWWPartners } from 'legacy/lut/classes/models/partner360/IWWPartners'
import {
  GROUP_ID_VERSION_PARAMETER,
  PARTNER_ID_VERSION_PARAMETER,
  URL_VERSION_PARAMETER,
} from 'legacy/lut/components/widgets/public/partner360/ELT/configs'
import { useEltDataSettings } from 'legacy/lut/components/widgets/public/partner360/ELT/context/EltDataSettingsContext'
import { getBusinessPerformanceData } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/businessPerformance/getBusinessPerformanceData'
import { getForceUpdatedForm } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/getForceUpdatedForm'
import { getNewFormatBalanceOfTrade } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/getNewFormatBalanceOfTrade'
import { isDocType } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/isDocType'
import { getPreparedFormDataForSaving } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/save/getPreparedFormDataForSaving'
import { uploadImagesFromPrevVersion } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/uploadImagesFromPrevVersion'
import { useEltRouting } from 'legacy/lut/components/widgets/public/partner360/ELT/hooks/useEltRouting'
import { useJbpSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { deleteParamsFromUrl, getUrlWithParams } from 'core-components/helpers/url'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import MeasureController from 'core/analytics/MeasureController'
import { useMeasureController } from 'core/analytics/useMeasureController'
import { saveFile } from 'core/classes/helpers/saveFiles'
import Logger from 'core/utils/Logger'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { isExistsChangedSteps } from '../helpers/changedStep/isExistsChangedSteps'
import { getImages } from '../helpers/getImages'
import { getLastGuideStep, getLastStep } from '../helpers/getLastStep'
import { getNewVersionData } from '../helpers/getNewVersionData'
import { isHasPlcData } from '../helpers/isHasPlcData'
import { getFormInfo } from '../helpers/requestsHelpers/formInfo/getFormInfo'
import { save } from '../helpers/requestsHelpers/save'
import { EltErrorStatuses } from '../models/EltErrorStatuses'
import { IFetchFormDataArgs } from '../models/IFetchFormDataArgs'
import { IForceUpdateData } from '../models/IForceUpdateData'
import { TImages, TImagesFields, TImageUpload, TMergedImages } from '../models/ImageUpload'
import { IPartnerGroupAdditionalInfo } from '../models/IPartnerGroupAdditionalInfo'
import {
  getNewBalanceOfTradeData,
  getObservedItemsFromRevenue,
} from '../ui/balanceOfTrade/hooks/useBoTComputedFields'
import { useRoundTable } from '../ui/roundTable/hooks/useRoundTable'
import { IAddNewVersion } from '../ui/versions/addNewVersionDialog/models'
import { getEntityDependsOnSelectMode } from '../ui/versions/helpers/getEntityDependsOnSelectMode'
import { TSelectVersionMode } from '../ui/versions/types/Types'

import { useDetectChangedSteps } from './useDetectChangedSteps'
import { useEventBusSubscribers } from './useEventBusSubscribers'
import { useForceUpdate } from './useForceUpdate'

interface IProps {
  partnerId: string
  metrics?: MeasureController
  versionId: string
  urlParameterForGroupVersions: string
  urlParameterForPartnerVersions: string
}

export const useEltData = ({
  partnerId,
  metrics,
  versionId,
  urlParameterForPartnerVersions,
  urlParameterForGroupVersions,
}: IProps) => {
  const user = useSelector(selectUser)

  const {
    jbpSettings: { fYearELT: yearForSave, fQuarterELT: quarterForSave },
  } = useJbpSettings()

  const navigate = useNavigate()

  const { startLoadData, stopLoadData } = useMeasureController(metrics || 'useUltData')
  const [data, setData] = useState<IELTData>()

  const { dataSettings, setDataSettings } = useEltDataSettings()

  const [initialDataSettings, setInitialDataSettings] = useState<string>()

  const [initialData, setInitialData] = useState<TEltFormData>({} as TEltFormData)
  const [isInitLoading, setIsInitialLoading] = useState(true)
  const [wip, setWip] = useState(false)
  const [error, setError] = useState<EltErrorStatuses | undefined>()
  const [imagesForUpload, setImagesForUpload] = useState<TImageUpload>()
  const [showVersionsHandler, setShowVersionsHandler] = useState(false)
  const [partnerGroupInfo, setPartnerGroupInfo] = useState<IPartnerGroupAdditionalInfo>({
    membersCount: 0,
    partnerId: null,
    groupName: null,
    groupId: null,
  })

  const [partnerGroups, setPartnerGroups] = useState<IPartnerGroupInfo[]>([])
  const [images, setImages] = useState<TImages>({} as TImages)
  const [partnerData, setPartnerData] = useState<IWWPartners | null>(null)
  const [dataForSave, setDataForSave] = useState<TEltFormData>({} as TEltFormData)
  const { rtEligible, onToggleRoundTable } = useRoundTable({
    data,
    partnerId,
    isRollup: dataForSave.IsRollup || 0,
    setData,
  })

  const [forceUpdateData, setForceUpdateData] = useState({} as IForceUpdateData)
  const { changedSteps } = useDetectChangedSteps({
    initialData,
    dataForSave,
    eltType: data?.EltType,
    initialDataSettings,
    dataSettings: data?.Settings,
  })

  const fetch = useCallback(
    async ({
      partnerId,
      forceUpdate = false,
      groupData,
      existingPartnerGroupInfo,
      forceShowVersionList,
    }: IFetchFormDataArgs) => {
      if (yearForSave && quarterForSave) {
        startLoadData()
        setIsInitialLoading(true)
        if (partnerId) {
          try {
            const {
              item,
              formData,
              partnerGroupData,
              partnerDataRes,
              showVersionsHandler,
              forceUpdateData,
            } = await getFormInfo({
              partnerId,
              forceUpdate,
              groupData,
              quarterForSave,
              yearForSave,
              existingPartnerGroupInfo,
            })
            if (partnerGroupData) {
              setPartnerGroupInfo(partnerGroupData)
            } else {
              setPartnerData(partnerDataRes)
            }
            if (showVersionsHandler || forceShowVersionList) {
              setShowVersionsHandler(true)
            } else {
              setForceUpdateData(forceUpdateData)
              setShowVersionsHandler(false)
              setInitialData(JSON.parse(JSON.stringify(formData)))
              setDataForSave(JSON.parse(JSON.stringify(formData)))
              setData(item)
              if (item?.Settings) {
                const newDataSettings = JSON.parse(item.Settings)
                setDataSettings(newDataSettings)
              }
              setInitialDataSettings(item?.Settings)
              setImages(
                getImages({
                  entryId: item?.EntryId,
                  logo: item?.Logo,
                  picture: item?.Picture,
                  attachment: item?.Attachment,
                  uid: item?.UID,
                })
              )
            }
            if (partnerGroupData) {
              navigate(getUrlWithParams({ [GROUP_ID_VERSION_PARAMETER]: partnerGroupData.uid }))
            } else {
              navigate(getUrlWithParams({ [PARTNER_ID_VERSION_PARAMETER]: partnerId }))
            }
          } catch (error: any) {
            setError(error.message)
          }
          setIsInitialLoading(false)
        }
        stopLoadData()
      }
    },
    [navigate, quarterForSave, startLoadData, stopLoadData, yearForSave]
  )

  const addNewVersion = useCallback(
    async (args: IAddNewVersion) => {
      if (partnerId) {
        setWip(true)

        const { formData, forceUpdateData, item } = await getNewVersionData(args)
        setForceUpdateData(forceUpdateData)
        setShowVersionsHandler(false)
        setInitialData(JSON.parse(JSON.stringify(formData)))
        setDataForSave(JSON.parse(JSON.stringify(formData)))
        setData(item)
        if (item?.Settings) {
          const newDataSettings = JSON.parse(item.Settings)
          setDataSettings(newDataSettings)
        }
        setInitialDataSettings(item?.Settings)

        setImages({} as TImages)
      }
      setWip(false)
    },
    [partnerId]
  )

  const onNetNdpChange = useCallback(
    (netNdpValue: ActualsValueTypes) => {
      let settings = dataSettings
      try {
        if (data?.Settings) {
          settings = JSON.parse(data.Settings)
        }
      } catch (error) {
        Logger.error('unable to parse data.Settings from IELTData', error)
      }
      settings.NetNdp = netNdpValue

      setDataSettings(settings)
      setData((prevState) => {
        if (!prevState) {
          return
        }

        return {
          ...prevState,
          Settings: JSON.stringify(settings),
        }
      })
    },
    [data, dataSettings]
  )

  const onChangePrimitiveFormData =
    (section?: ELT_FORM_SECTION) => (field: string) => (value: string | number) => {
      if (section) {
        setDataForSave((dataForSave) => ({
          ...dataForSave,
          [section]: { ...dataForSave?.[section], [field]: value },
        }))
      } else {
        setDataForSave((formData) => ({
          ...formData,
          [field]: value,
        }))
      }
    }

  const onChangeComplexFormData = useCallback(
    (section: ELT_FORM_SECTION) => (data: TComplexSectionData) => {
      setDataForSave((dataForSave) => ({
        ...dataForSave,
        [section]: Array.isArray(data) ? [...data] : data,
      }))
    },
    []
  )

  const updateData = useCallback((field: string, value: string | number) => {
    setData((data) => ({ ...(data || {}), [field]: value }) as IELTData)
  }, [])

  const onSave = useCallback(
    (saveMode: 'guide' | 'formStep') => async (step?: number, finalized?: number) => {
      setWip(true)
      try {
        //TODO: optimize method
        if (saveMode === 'formStep') {
          const hasModifiedData = isExistsChangedSteps(changedSteps)
          const isPolyFinancialsStepExist = Boolean(
            (dataForSave as IELTFormData).AdditionalSections?.RevenuePoly
          )
          const isBusinessPerformanceTabEnabled = Boolean(
            (dataForSave as IEltDocFormData).IsBusinessPerformanceTabEnabled
          )

          const isDoc = data?.EltType ? isDocType(data?.EltType) : false

          const preparedFormData: TEltFormData = getPreparedFormDataForSaving({
            isDoc,
            dataForSave,
          })

          const nextStep =
            step ||
            getLastStep({
              currentStep: data?.LastStep || 1,
              isPolyFinancialsStepExist,
              isBusinessPerformanceTabEnabled,
              isHasPlcData: isHasPlcData(preparedFormData),
            })
          const res = await save({
            data: data,
            formData: preparedFormData,
            additionalData: {
              LastStep: nextStep,
              Finalized: finalized,
            },
            userEmail: user.email,
            hasModifiedData,
          })
          const entryId = res?.[0]?.EntryId
          const uid = res?.[0]?.UID
          if (entryId && imagesForUpload) {
            const imagesForUploadFields = Object.keys(imagesForUpload)
            for (const fieldName of imagesForUploadFields) {
              const imageInfo = imagesForUpload[fieldName]
              if (imageInfo.file) {
                await saveFile({
                  formId: FORM_ID,
                  formCode: FORM_CODE,
                  fieldName: fieldName,
                  models: [{ file: imageInfo.file, EntryId: entryId, UID: uid }],
                })
              }
            }
            setImagesForUpload(undefined)
            setImages(
              getImages({
                entryId,
                logo: data?.Logo,
                picture: data?.Picture,
                attachment: data?.Attachment,
                uid: uid,
              })
            )
          }
          updateData('EntryId', entryId)
          updateData('UID', uid)
          updateData('LastStep', nextStep)
          setInitialData(JSON.parse(JSON.stringify(preparedFormData)))
          setInitialDataSettings(data?.Settings)
          setDataForSave(preparedFormData)
        }
        if (saveMode === 'guide') {
          const guidesWasRead = Number((data?.GuidesStep || 1) + 1 > 2)
          const res = await save({
            data,
            formData: dataForSave,
            additionalData: {
              GuidesStep: getLastGuideStep(data?.GuidesStep || 1),
              GuidelinesWasRead: guidesWasRead,
            },
            userEmail: user.email,
          })
          const item: IELTData | null = res.length > 0 ? res[0] : null
          const entryId = res?.[0]?.EntryId
          const uid = res?.[0]?.UID
          updateData('EntryId', entryId)
          updateData('UID', uid)
          updateData('GuidesStep', getLastGuideStep(data?.GuidesStep || 1))
          updateData('GuidelinesWasRead', guidesWasRead)
          if (item && !item.Logo && !item.Picture) {
            const { pictureUrlCurrentDoc, logoUrlCurrentDoc } = await uploadImagesFromPrevVersion({
              partnerId: item.PartnerId,
              groupId: item.GroupId,
              eltType: item.EltType,
              item,
            })

            setImages(
              getImages({
                entryId: item?.EntryId,
                logo: logoUrlCurrentDoc,
                picture: pictureUrlCurrentDoc,
                uid: item?.UID,
              })
            )
          }

          navigate(
            getUrlWithParams(
              { [URL_VERSION_PARAMETER]: uid },
              deleteParamsFromUrl([GROUP_ID_VERSION_PARAMETER, PARTNER_ID_VERSION_PARAMETER])
            )
          )
        }
      } finally {
        setWip(false)
      }
    },
    [changedSteps, data, dataForSave, imagesForUpload, navigate, updateData, user.email]
  )

  const finalizeVersion = useCallback(
    async (saveData?: boolean) => {
      updateData('Finalized', 1)
      if (saveData) {
        onSave('formStep')(data?.LastStep || 1, 1)
      } else {
        setWip(true)
        await save({
          data,
          formData: initialData,
          additionalData: {
            LastStep: data?.LastStep || 1,
            Finalized: 1,
          },
          userEmail: user.email,
        })
        setDataForSave(initialData)
        setWip(false)
      }
    },
    [updateData, onSave, data, user.email, initialData]
  )

  const onImageUpload = (fieldName: string, file: File, fileName: string) => {
    setImagesForUpload((state) => ({ ...(state || {}), [fieldName]: { file, fileName } }))
    if (!file) {
      setImages((state) => ({ ...state, [fieldName]: '' }))
    }
  }

  const selectVersion = useCallback(
    (mode: TSelectVersionMode) =>
      async (selectedVersion: IELTData, versionName?: string, versionSerialNumber?: number) => {
        setIsInitialLoading(true)
        const data = await getEntityDependsOnSelectMode({
          onImageUpload,
          selectedVersion,
          mode,
          versionName,
          versionSerialNumber,
        })
        setImages(
          getImages({
            entryId: selectedVersion.EntryId,
            logo: selectedVersion.Logo,
            picture: selectedVersion.Picture,
            attachment: selectedVersion?.Attachment,
            uid: selectedVersion.UID,
          })
        )
        const formData = parseJson<TEltFormData>(
          data.FormData?.replace(/class="\\&quot;editor-block\\&quot;"/g, '')?.replace(
            /class="\\&quot;editor-block-content\\&quot;"/g,
            ''
          ) //remove non safe json data
        )
        if (data.EltType === EltType.Pptx && formData) {
          const pptxFormData = formData as IELTFormData
          formData.BalanceOfTrade = getNewFormatBalanceOfTrade(
            formData?.BalanceOfTrade,
            pptxFormData?.Revenue
          )
        }

        setData(data)
        if (data?.Settings) {
          const newDataSettings = JSON.parse(data.Settings)
          setDataSettings(newDataSettings)
        }
        setInitialDataSettings(data?.Settings)
        setInitialData(JSON.parse(JSON.stringify(formData)))
        setDataForSave(JSON.parse(JSON.stringify(formData)))

        if (mode === 'clone') {
          const res = await save({
            data,

            formData: JSON.parse(JSON.stringify(formData)),

            additionalData: {
              LastStep: 1,
              Finalized: 0,
            },
            userEmail: user.email,
          })
          const entryId = res?.[0]?.EntryId
          const uid = res?.[0]?.UID
          if (entryId && imagesForUpload) {
            const imagesForUploadFields = Object.keys(imagesForUpload)
            for (const fieldName of imagesForUploadFields) {
              const imageInfo = imagesForUpload[fieldName]
              if (imageInfo.file) {
                await saveFile({
                  formId: FORM_ID,
                  formCode: FORM_CODE,
                  fieldName: fieldName,
                  models: [{ file: imageInfo.file, EntryId: entryId, UID: uid }],
                })
              }
            }
            setImagesForUpload(undefined)

            setImages(
              getImages({
                entryId,
                logo: data?.Logo,
                picture: data?.Picture,
                attachment: data?.Attachment,
                uid: uid,
              })
            )
          }
          updateData('EntryId', entryId)
          updateData('UID', uid)
          navigate(getUrlWithParams({ [URL_VERSION_PARAMETER]: uid }))
        } else {
          navigate(
            getUrlWithParams(
              { [URL_VERSION_PARAMETER]: selectedVersion.UID },
              deleteParamsFromUrl([PARTNER_ID_VERSION_PARAMETER, GROUP_ID_VERSION_PARAMETER])
            )
          )
        }

        setIsInitialLoading(false)
        setShowVersionsHandler(false)
      },
    [imagesForUpload, navigate, updateData, user.email]
  )

  const forceUpdateFullForm = useCallback(
    async (newYear: number, newQuarter: number, isRollup?: boolean) => {
      if (data) {
        const rollupValue = isRollup === undefined ? dataForSave.IsRollup || 0 : Number(isRollup)
        const { sellThruSellToDescs, forceUpdatedFormData, forceUpdatedItem } =
          await getForceUpdatedForm({
            item: { ...data },
            formData: { ...dataForSave, IsRollup: rollupValue },
            partnerId,
            partnerGroupData: partnerGroupInfo,
            fQuarter: newQuarter,
            fYear: newYear,
            dataSettings,
          })

        const newDataSettings = JSON.parse(forceUpdatedItem.Settings)
        setDataSettings(newDataSettings)

        const isPolyFinancialsStepExist = Boolean(
          (dataForSave as IELTFormData).AdditionalSections?.RevenuePoly
        )
        const isBusinessPerformanceTabEnabled = Boolean(
          (dataForSave as IEltDocFormData).IsBusinessPerformanceTabEnabled
        )
        const nextStep = getLastStep({
          currentStep: data?.LastStep || 1,
          isPolyFinancialsStepExist,
          isHasPlcData: isHasPlcData(dataForSave),
          isBusinessPerformanceTabEnabled,
        })

        if (data.EltType === EltType.Pptx) {
          const pptxFormData = forceUpdatedFormData as IELTFormData
          const observedItems = getObservedItemsFromRevenue(pptxFormData.Revenue)
          forceUpdatedFormData.BalanceOfTrade = getNewBalanceOfTradeData(
            observedItems,
            pptxFormData.BalanceOfTrade
          )
        }

        await save({
          data: forceUpdatedItem,

          formData: forceUpdatedFormData,

          additionalData: {
            LastStep: nextStep,
          },
          userEmail: user.email,
        })

        setForceUpdateData(sellThruSellToDescs)
        setData(forceUpdatedItem)
        setInitialData(JSON.parse(JSON.stringify(forceUpdatedFormData)))
        setDataForSave(JSON.parse(JSON.stringify(forceUpdatedFormData)))
      }
    },
    [data, dataForSave, partnerId, partnerGroupInfo, dataSettings, user.email]
  )

  const { fetchEltData } = useEltRouting({
    versionId,
    urlParameterForGroupVersions,
    urlParameterForPartnerVersions,
    data,
    partnerId,
    showVersionsHandler,
    selectVersion,
    fetch,
    setPartnerGroupInfo,
    setIsInitialLoading,
    setPartnerGroups,
  })

  useEffect(() => {
    fetchEltData()
  }, [fetchEltData])

  const { forceUpdate, forceUpdateFormToActualData } = useForceUpdate({
    onChangeComplexFormData,
    fYear: data?.FYear,
    partnerId,
    groupId: partnerGroupInfo.groupId,
    setWip,
    dataForSave,
    updateData,
    forceUpdateFullForm,
    eltType: data?.EltType,
    currentStep: data?.LastStep,
    uid: data?.UID,
  })

  useEventBusSubscribers({
    setWip,
    setPartnerGroupInfo,
    partnerGroupInfo,
    updateData,
  })

  const showVersionsList = useCallback(() => {
    navigate(deleteParamsFromUrl([URL_VERSION_PARAMETER]))
    if (partnerGroupInfo.uid) {
      navigate(getUrlWithParams({ [GROUP_ID_VERSION_PARAMETER]: partnerGroupInfo.uid }))
      fetch({ partnerId, existingPartnerGroupInfo: partnerGroupInfo, forceShowVersionList: true })
      return
    }
    navigate(getUrlWithParams({ [PARTNER_ID_VERSION_PARAMETER]: partnerId }))
    fetch({ partnerId, forceShowVersionList: true })
  }, [fetch, navigate, partnerGroupInfo, partnerId])

  const displayedImages = useMemo<TMergedImages>(() => {
    const keys: TImagesFields[] = ['Picture', 'Logo', 'Attachment']

    return keys.reduce((acc, key) => {
      const file = imagesForUpload?.[key]?.file
      const src = images[key]

      acc[key] = {}

      if (file) {
        acc[key].file = file
      }

      if (src) {
        acc[key].src = src
      }

      return acc
    }, {} as TMergedImages)
  }, [images, imagesForUpload])

  const onToggleRollupMode = useCallback(
    async (value: boolean) => {
      onChangePrimitiveFormData()('IsRollup')(Number(value))
      if (data?.FYear && data?.FQuarter) {
        setWip(true)
        await forceUpdateFullForm(data?.FYear, data?.FQuarter, value)
        setWip(false)
      }
    },
    [data?.FYear, data?.FQuarter, forceUpdateFullForm]
  )

  const onToggleBusinessPerformance = useCallback(
    async (value: boolean) => {
      onChangePrimitiveFormData()('IsBusinessPerformanceTabEnabled')(Number(value))

      if (
        !value ||
        !data?.FYear ||
        (dataForSave as IEltDocFormData).BusinessPerformanceData?.length > 0
      ) {
        return
      }

      setWip(true)

      try {
        const businessPerfData = await getBusinessPerformanceData({
          fQuarter: data.FQuarter,
          fYear: data.FYear,
          partnerId,
        })
        onChangeComplexFormData(ELT_FORM_SECTION.BUSINESS_PERF_DATA)(businessPerfData)
      } finally {
        setWip(false)
      }
    },
    [data?.FYear, data?.FQuarter, dataForSave, partnerId, onChangeComplexFormData]
  )

  return {
    data,
    wip,
    error,
    formData: dataForSave,
    dataSettings,
    onNetNdpChange,
    onChangePrimitiveFormData,
    onChangeComplexFormData,
    onSave,
    onImageUpload,
    images,
    updateData,
    showVersionsHandler,
    selectVersion,
    partnerGroupInfo,
    forceUpdate,
    partnerGroups,
    fetch,
    changedSteps,
    partnerData,
    addNewVersion,
    finalizeVersion,
    formIsFinalized: data?.Finalized === 1,
    forceUpdateData,
    showVersionsList,
    forceUpdateFormToActualData,
    isInitLoading,
    displayedImages,
    onToggleBusinessPerformance,
    onToggleRollupMode,
    rtEligible,
    onToggleRoundTable,
  }
}
